import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  ProgressBar,
  Badge,
} from "react-bootstrap";

import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";

const ASPECT_RATIOS = {
  landscape: { size: "1280x720", label: "Landscape", width: 1280, height: 720, icon: "\u25AD" },
  portrait: { size: "720x1280", label: "Portrait", width: 720, height: 1280, icon: "\u25AF" },
  wide: { size: "1792x1024", label: "Cinematic Wide", width: 1792, height: 1024, icon: "\u2B1C" },
  tall: { size: "1024x1792", label: "Tall", width: 1024, height: 1792, icon: "\u2B1C" },
};

const STATUS_LABELS = {
  queued: "Queued",
  in_progress: "Generating",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_VARIANTS = {
  queued: "secondary",
  in_progress: "warning",
  completed: "success",
  failed: "danger",
};

function VideoGeneration({ openaiApiKey, setErrorMessage, activeTab, switchTab }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("sora-2");
  const [duration, setDuration] = useState("4");
  const [aspectRatio, setAspectRatio] = useState("landscape");
  const [videos, setVideos] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [expandedVideoIndex, setExpandedVideoIndex] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);

  const promptRef = useRef(null);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);
  const videosRef = useRef(videos);

  useEffect(() => {
    videosRef.current = videos;
  }, [videos]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      videosRef.current.forEach((v) => {
        if (v.videoUrl && v.videoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(v.videoUrl);
        }
      });
    };
  }, []);

  // Focus prompt when switching to this tab
  useEffect(() => {
    if (activeTab === "video" && promptRef.current && videos.length === 0) {
      promptRef.current.focus();
    }
  }, [activeTab]);

  const formatElapsedTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const startTimer = () => {
    setElapsedTime(0);
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startPolling = useCallback(
    (videoId) => {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await fetch(
            `https://api.openai.com/v1/videos/${videoId}`,
            {
              headers: {
                Authorization: `Bearer ${openaiApiKey}`,
              },
            }
          );

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(
              errData.error?.message || `Status check failed (${response.status})`
            );
          }

          const data = await response.json();

          setVideos((prev) =>
            prev.map((v) =>
              v.id === videoId
                ? {
                    ...v,
                    status: data.status,
                    progress: data.progress || v.progress,
                  }
                : v
            )
          );

          if (data.status === "completed") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            stopTimer();
            setIsGenerating(false);
            await downloadVideoContent(videoId);
          } else if (data.status === "failed") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            stopTimer();
            setIsGenerating(false);
            if (setErrorMessage) {
              setErrorMessage(
                "Video generation failed. Try a different prompt or shorter duration."
              );
            }
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 10000);
    },
    [openaiApiKey]
  );

  const downloadVideoContent = async (videoId) => {
    try {
      const response = await fetch(
        `https://api.openai.com/v1/videos/${videoId}/content`,
        {
          headers: {
            Authorization: `Bearer ${openaiApiKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download video");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setVideos((prev) => {
        const updated = prev.map((v) =>
          v.id === videoId ? { ...v, videoUrl: url, status: "completed" } : v
        );
        const idx = updated.findIndex((v) => v.id === videoId);
        if (idx !== -1) {
          setExpandedVideoIndex(idx);
        }
        return updated;
      });
    } catch (err) {
      console.error("Download error:", err);
      if (setErrorMessage) {
        setErrorMessage("Video generated but failed to download. Try refreshing.");
      }
    }
  };

  const handleGenerate = async (e) => {
    e?.preventDefault();
    if (!prompt.trim() || isGenerating) return;

    if (!openaiApiKey) {
      if (setErrorMessage) {
        setErrorMessage("No API key configured. Please contact your instructor.");
      }
      return;
    }

    setIsGenerating(true);
    startTimer();

    try {
      const formData = new FormData();
      formData.append("model", model);
      formData.append("prompt", prompt.trim());
      formData.append("seconds", duration);
      formData.append("size", ASPECT_RATIOS[aspectRatio].size);

      const response = await fetch("https://api.openai.com/v1/videos", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${openaiApiKey}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error?.message || `Request failed (${response.status})`
        );
      }

      const data = await response.json();

      const newVideo = {
        id: data.id,
        prompt: prompt.trim(),
        model: model,
        duration: duration,
        size: ASPECT_RATIOS[aspectRatio].size,
        aspectRatio: aspectRatio,
        status: data.status || "queued",
        progress: 0,
        videoUrl: null,
        createdAt: new Date().toISOString(),
      };

      setVideos((prev) => [...prev, newVideo]);
      startPolling(data.id);
    } catch (err) {
      setIsGenerating(false);
      stopTimer();
      if (setErrorMessage) {
        setErrorMessage(err.message || "Failed to create video");
      }
    }
  };

  const handleDownloadToDevice = (video) => {
    if (!video.videoUrl) return;
    const a = document.createElement("a");
    a.href = video.videoUrl;
    a.download = `sora-video-${video.id.slice(-8)}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRemix = (video) => {
    setPrompt(video.prompt);
    setModel(video.model);
    setDuration(video.duration);
    setAspectRatio(video.aspectRatio || "landscape");
    if (promptRef.current) {
      promptRef.current.focus();
    }
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  const handleDelete = (index) => {
    setVideos((prev) => {
      const video = prev[index];
      if (video.videoUrl && video.videoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(video.videoUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
    if (expandedVideoIndex === index) {
      setExpandedVideoIndex(null);
    } else if (expandedVideoIndex !== null && expandedVideoIndex > index) {
      setExpandedVideoIndex(expandedVideoIndex - 1);
    }
  };

  const handleClear = () => {
    if (clearConfirm) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      stopTimer();
      setIsGenerating(false);
      videos.forEach((v) => {
        if (v.videoUrl && v.videoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(v.videoUrl);
        }
      });
      setVideos([]);
      setExpandedVideoIndex(null);
      setClearConfirm(false);
    } else {
      setClearConfirm(true);
      setTimeout(() => setClearConfirm(false), 5000);
    }
  };

  // --- Render sections ---

  const renderGeneratingIndicator = () => {
    const activeVideo = videos.find(
      (v) => v.status === "queued" || v.status === "in_progress"
    );
    if (!activeVideo) return null;

    return (
      <div className="video-generating-indicator">
        <div className="video-generating-inner">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <Spinner animation="border" size="sm" style={{ color: "#f05f40" }} />
            <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
              Generating your video...
            </span>
            <Badge bg={STATUS_VARIANTS[activeVideo.status]}>
              {STATUS_LABELS[activeVideo.status]}
            </Badge>
          </div>
          <ProgressBar
            now={activeVideo.progress || (activeVideo.status === "queued" ? 5 : 50)}
            animated
            striped
            className="video-progress-bar"
            label={activeVideo.progress ? `${activeVideo.progress}%` : ""}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
            <span style={{ fontSize: "0.85rem", color: "#666" }}>
              {activeVideo.status === "queued"
                ? "Waiting in queue..."
                : "AI is rendering your video"}
            </span>
            <span style={{ fontSize: "0.85rem", color: "#999", fontFamily: "monospace" }}>
              {formatElapsedTime(elapsedTime)}
            </span>
          </div>
          <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: "4px" }}>
            "{activeVideo.prompt.length > 80
              ? activeVideo.prompt.slice(0, 80) + "..."
              : activeVideo.prompt}"
          </div>
        </div>
      </div>
    );
  };

  const renderVideoCard = (video, index) => {
    const isExpanded = expandedVideoIndex === index;
    const isCompleted = video.status === "completed" && video.videoUrl;
    const isFailed = video.status === "failed";
    const isProcessing =
      video.status === "queued" || video.status === "in_progress";

    return (
      <div key={video.id || index} className="video-gallery-item">
        {/* Prompt bubble */}
        <div className="video-prompt-bubble">
          <div style={{ color: "#ddd", fontWeight: "bold", fontSize: "12px", fontStyle: "italic", marginBottom: "5px" }}>
            Prompt for Sora 2:
          </div>
          {video.prompt}
        </div>

        {/* Video player or status */}
        <div className="video-player-container">
          {isCompleted ? (
            <div
              className={`video-player-wrapper ${isExpanded ? "video-expanded" : ""}`}
              onClick={() =>
                setExpandedVideoIndex(isExpanded ? null : index)
              }
            >
              <video
                controls
                playsInline
                preload="metadata"
                className="video-player"
                onClick={(e) => e.stopPropagation()}
              >
                <source src={video.videoUrl} type="video/mp4" />
                Your browser does not support video playback.
              </video>
            </div>
          ) : isProcessing ? (
            <div className="video-placeholder-processing">
              <Spinner animation="border" style={{ color: "#f05f40" }} />
              <div style={{ marginTop: "10px", fontWeight: "bold" }}>
                {STATUS_LABELS[video.status]}
              </div>
              {video.progress > 0 && (
                <ProgressBar
                  now={video.progress}
                  animated
                  striped
                  className="video-progress-bar"
                  style={{ width: "80%", marginTop: "10px" }}
                  label={`${video.progress}%`}
                />
              )}
            </div>
          ) : isFailed ? (
            <div className="video-placeholder-failed">
              <div style={{ fontSize: "2rem" }}>!</div>
              <div style={{ marginTop: "5px", fontWeight: "bold", color: "#dc3545" }}>
                Generation Failed
              </div>
            </div>
          ) : null}
        </div>

        {/* Metadata */}
        <div className="video-meta-row">
          <Badge bg={STATUS_VARIANTS[video.status]} className="video-status-badge">
            {STATUS_LABELS[video.status]}
          </Badge>
          <span className="video-meta-info">
            {video.model === "sora-2-pro" ? "Sora 2 Pro" : "Sora 2"} &middot;{" "}
            {video.duration}s &middot;{" "}
            {ASPECT_RATIOS[video.aspectRatio]?.label || video.size}
          </span>
        </div>

        {/* Action buttons */}
        <div className="video-actions">
          {isCompleted && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="btn-video-action"
                onClick={() =>
                  setExpandedVideoIndex(isExpanded ? null : index)
                }
              >
                {isExpanded ? "Collapse" : "Expand"}
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                className="btn-video-action"
                onClick={() => handleDownloadToDevice(video)}
              >
                Download
              </Button>
            </>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="btn-video-action"
            onClick={() => handleRemix(video)}
          >
            Remix
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="btn-video-action"
            onClick={() => handleDelete(index)}
          >
            Delete
          </Button>
        </div>
      </div>
    );
  };

  const renderGallery = () => {
    if (videos.length === 0) return null;

    return (
      <div className="video-gallery">
        {videos.map((video, index) => renderVideoCard(video, index))}
      </div>
    );
  };

  const renderAspectRatioSelector = () => {
    return (
      <div className="video-aspect-selector">
        {Object.entries(ASPECT_RATIOS).map(([key, value]) => (
          <div
            key={key}
            className={`video-aspect-option ${aspectRatio === key ? "video-aspect-selected" : ""}`}
            onClick={() => setAspectRatio(key)}
          >
            <div
              className="video-aspect-preview"
              style={{
                aspectRatio: `${value.width} / ${value.height}`,
              }}
            />
            <div className="video-aspect-label">{value.label}</div>
            <div className="video-aspect-size">{value.size}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header
              as="h4"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              Generate Videos with Sora 2
              {videos.length > 0 && (
                <Button
                  size="sm"
                  style={{
                    fontSize: "0.65rem",
                    padding: "0.15rem 0.4rem",
                  }}
                  variant={clearConfirm ? "danger" : "outline-secondary"}
                  onClick={handleClear}
                  className="btn-video-action"
                >
                  {clearConfirm ? "Confirm Clear" : "Clear Videos"}
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {/* Instruction box */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "2px solid #f05f40",
                  borderRadius: "8px",
                  padding: "10px 15px 15px 15px",
                  margin: "10px 0",
                  boxShadow: "0 0 10px rgba(240, 95, 64, 0.3)",
                }}
              >
                <h3
                  style={{
                    color: "#999",
                    fontWeight: "bold",
                    margin: "0 0 5px 0",
                    fontSize: "12px",
                    fontStyle: "italic",
                  }}
                >
                  Instructions:
                </h3>
                <h4
                  style={{
                    color: "#f05f40ff",
                    fontWeight: "bold",
                    margin: "0 0 5px 0",
                  }}
                >
                  Create AI-generated videos from text descriptions using
                  OpenAI's Sora 2.
                </h4>
                <p
                  style={{
                    color: "#f05f40ff",
                    fontWeight: "bold",
                    margin: "0",
                  }}
                >
                  Describe the scene, camera movement, lighting, and mood. Be
                  specific for best results.
                </p>
              </div>

              {/* Generating indicator */}
              {isGenerating && renderGeneratingIndicator()}

              {/* Video gallery */}
              {renderGallery()}

              {/* Separator */}
              {videos.length > 0 && <hr style={{ marginTop: "30px" }} />}

              {/* Prompt input */}
              <Form onSubmit={handleGenerate}>
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      left: "50px",
                      color: "#999",
                      fontWeight: "bold",
                      fontSize: "12px",
                      fontStyle: "italic",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  >
                    Prompt for {model === "sora-2-pro" ? "Sora 2 Pro" : "Sora 2"}:
                  </div>
                  <Form.Control
                    ref={promptRef}
                    as="textarea"
                    rows={3}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="A serene underwater scene with bioluminescent jellyfish floating through a coral reef, cinematic lighting, slow camera dolly..."
                    className="form-control orange-highlight"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate(e);
                      }
                    }}
                    onInput={(e) => {
                      const textarea = e.target;
                      textarea.style.height = "auto";
                      textarea.style.height =
                        Math.max(64, textarea.scrollHeight) + "px";
                    }}
                    style={{
                      minHeight: "4em",
                      position: "relative",
                      width: "100%",
                      resize: "none",
                      overflow: "hidden",
                    }}
                    disabled={isGenerating}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    className="btn-video-generate"
                    disabled={isGenerating || !prompt.trim()}
                  >
                    {isGenerating ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Go"
                    )}
                  </Button>
                </div>

                {/* Settings row */}
                <div className="video-settings-row">
                  {/* Model selection */}
                  <div className="video-setting-group">
                    <span className="video-setting-label">Model:</span>
                    <Form.Check
                      inline
                      type="radio"
                      name="video-model"
                      id="model-sora2"
                      label="Sora 2 (Fast)"
                      value="sora-2"
                      checked={model === "sora-2"}
                      onChange={(e) => setModel(e.target.value)}
                      style={{ fontSize: "12px" }}
                      disabled={isGenerating}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      name="video-model"
                      id="model-sora2-pro"
                      label="Sora 2 Pro (Quality)"
                      value="sora-2-pro"
                      checked={model === "sora-2-pro"}
                      onChange={(e) => setModel(e.target.value)}
                      style={{ fontSize: "12px" }}
                      disabled={isGenerating}
                    />
                  </div>

                  {/* Duration selection */}
                  <div className="video-setting-group">
                    <span className="video-setting-label">Duration:</span>
                    <Form.Check
                      inline
                      type="radio"
                      name="video-duration"
                      id="duration-4"
                      label="4 sec"
                      value="4"
                      checked={duration === "4"}
                      onChange={(e) => setDuration(e.target.value)}
                      style={{ fontSize: "12px" }}
                      disabled={isGenerating}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      name="video-duration"
                      id="duration-8"
                      label="8 sec"
                      value="8"
                      checked={duration === "8"}
                      onChange={(e) => setDuration(e.target.value)}
                      style={{ fontSize: "12px" }}
                      disabled={isGenerating}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      name="video-duration"
                      id="duration-12"
                      label="12 sec"
                      value="12"
                      checked={duration === "12"}
                      onChange={(e) => setDuration(e.target.value)}
                      style={{ fontSize: "12px" }}
                      disabled={isGenerating}
                    />
                  </div>
                </div>

                {/* Aspect ratio selector */}
                <div className="video-setting-group" style={{ marginTop: "8px" }}>
                  <span className="video-setting-label">Aspect Ratio:</span>
                  {renderAspectRatioSelector()}
                </div>
              </Form>

              {videos.length > 0 && (
                <>
                  <hr style={{ marginTop: "20px" }} />
                  <div style={{
                    backgroundColor: "white",
                    fontSize: "large",
                    border: "2px solid #f05f40",
                    borderRadius: "15px",
                    padding: "10px 15px 15px 15px",
                    margin: "15px 0 10px 0",
                    boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
                    width: window.innerWidth < 992 ? "100%" : "50%"
                  }}>
                    <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                      Instructions:
                    </h3>
                    <span style={{ margin: "0", lineHeight: "2.2" }}>
                      Generate more production elements for your film using{" "}
                      <Button
                        className="btn-nav-link"
                        onClick={() => switchTab('gpt')}
                        style={{
                          margin: "0 5px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          display: "inline",
                          whiteSpace: "nowrap",
                          backgroundColor: "white",
                          borderColor: "#f05f40",
                          color: "#f05f40",
                          verticalAlign: "middle",
                        }}
                      >
                        Text Generation
                      </Button>
                    </span>
                  </div>
                  <hr style={{ marginTop: "15px" }} />
                </>
              )}
            </Card.Body>

            {videos.length > 0 && (
              <Card.Footer
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  padding: "0.4rem 0.75rem",
                }}
              >
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <Button
                    size="sm"
                    style={{
                      fontSize: "0.65rem",
                      padding: "0.15rem 0.4rem",
                    }}
                    variant={clearConfirm ? "danger" : "outline-secondary"}
                    onClick={handleClear}
                    className="btn-video-action"
                  >
                    {clearConfirm ? "Confirm Clear" : "Clear Videos"}
                  </Button>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default VideoGeneration;
